// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CategoryItem from "@bg/App/Components/CategoryItem";
import { CustomAppState, xHeaderActions } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showAsCategoryTiles = "showAsCategoryTiles",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showAsCategoryTiles]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    categoriesDataView: getCategoriesDataView(state, { maxDepth: 1 }),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xHeaderActions> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CategoryListL1CategoriesStyles {
    categoryTitle?: TypographyPresentationProps;
}

export const categoryListL1CategoriesStyles: CategoryListL1CategoriesStyles = {
    categoryTitle: {
        variant: "h5",
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
};

const CategoryListL1Categories: React.FC<Props> = ({
    fields: { showAsCategoryTiles, title },
    categoriesDataView,
    loadCategories,
}) => {
    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories({ maxDepth: 1 });
        }
    }, []);

    const categories = categoriesDataView.value;

    if (!categories || categories.length < 1) {
        return <></>;
    }

    return (
        <>
            {showAsCategoryTiles ? (
                <StyledWrapper
                    css={css`
                        width: 100%;
                        display: flex;
                        justify-content: flex-start;
                        align-items: flex-start;
                        flex-wrap: wrap;
                    `}
                >
                    <GridContainer gap={30}>
                        {categories.map((category, index) => (
                            <GridItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                width={[12, 6, 4, 3, 3]}
                            >
                                <CategoryItem
                                    name={category.shortDescription}
                                    path={category.path}
                                    smallImagePath={category.smallImagePath}
                                />
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            ) : (
                <StyledWrapper
                    css={css`
                        padding-top: 30px;
                        padding-bottom: 11px;
                        border-top: 2px solid ${getColor("common.ui3")};
                        border-bottom: 2px solid ${getColor("common.ui3")};
                    `}
                >
                    <Typography {...categoryListL1CategoriesStyles.categoryTitle}>{title}</Typography>
                    <GridContainer gap={0}>
                        {categories.map((category, index) => (
                            <GridItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                width={12}
                            >
                                <Link
                                    href={category.path}
                                    css={css`
                                        font-size: ${fontConfig.fontSize.regular}px;
                                        line-height: 32px;
                                        font-weight: ${fontConfig.weight.semiBold};

                                        &,
                                        span {
                                            letter-spacing: 0;
                                        }
                                    `}
                                >
                                    {category.shortDescription}
                                </Link>
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            )}
        </>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Category List - L1 Categories",
    fieldDefinitions: [
        {
            name: fields.showAsCategoryTiles,
            displayName: "Show as Category Tiles",
            tooltip: "if it's not checked, it will show as Link List",
            fieldType: "General",
            editorTemplate: "CheckboxField",
            defaultValue: true,
        },
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isVisible: ({ fields }) => !fields.showAsCategoryTiles,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryListL1Categories),
    definition,
};

export default widgetModule;
